import { useState, useEffect, useRef } from "react";
import { Check, Minus } from "lucide-react";
import { COMPARISON, METRICS } from "../data/content";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, Overline } from "../components/Reveal";
import PageHeader from "../components/PageHeader";
import CTASection from "../components/CTASection";

function MetricBar({ label, value, delay }) {
  const [width, setWidth] = useState(0);
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setWidth(value), delay);
          let start = 0;
          const step = () => {
            start += Math.ceil(value / 40);
            if (start >= value) {
              setDisplay(value);
            } else {
              setDisplay(start);
              requestAnimationFrame(step);
            }
          };
          setTimeout(() => requestAnimationFrame(step), delay);
          obs.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, delay]);

  return (
    <div ref={ref} data-testid={`metric-bar-${value}`}>
      <div className="flex justify-between items-end mb-3">
        <span className="text-sm uppercase tracking-[0.15em] text-offwhite">
          {label}
        </span>
        <span className="font-serif text-3xl text-gold-gradient font-bold">
          {display}%
        </span>
      </div>
      <div className="h-2.5 bg-navy border border-gold/20 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-goldhover to-gold transition-[width] duration-[1500ms] ease-out"
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

export default function WhyUs() {
  const { lang, t } = useLang();
  const p = t.pages.whyUs;

  return (
    <div data-testid="why-medprime-page">
      <PageHeader
        overline={p.overline}
        title={p.titleA}
        accent={p.accent}
        subtitle={p.subtitle}
      />

      {/* COMPARISON TABLE */}
      <section className="pb-20 bg-navy">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <div className="overflow-x-auto border border-gold/20">
              <table className="w-full text-left min-w-[820px]" data-testid="comparison-table">
                <thead>
                  <tr className="bg-deepblack">
                    <th className="p-5 text-xs uppercase tracking-[0.15em] text-muted2 font-semibold">{p.cols.provider}</th>
                    <th className="p-5 text-xs uppercase tracking-[0.15em] text-muted2 font-semibold">{p.cols.price}</th>
                    <th className="p-5 text-xs uppercase tracking-[0.15em] text-muted2 font-semibold">{p.cols.language}</th>
                    <th className="p-5 text-xs uppercase tracking-[0.15em] text-muted2 font-semibold">{p.cols.local}</th>
                    <th className="p-5 text-xs uppercase tracking-[0.15em] text-muted2 font-semibold">{p.cols.model}</th>
                    <th className="p-5 text-xs uppercase tracking-[0.15em] text-muted2 font-semibold">{p.cols.weakness}</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.map((c, i) => (
                    <tr
                      key={c.name.en}
                      data-testid={`comparison-row-${i}`}
                      className={`border-t border-gold/10 ${
                        c.highlight
                          ? "bg-gold/[0.06]"
                          : i % 2 === 0
                          ? "bg-navy"
                          : "bg-deepblack/40"
                      }`}
                    >
                      <td className="p-5">
                        <span
                          className={`font-serif text-xl ${
                            c.highlight ? "text-gold" : "text-offwhite"
                          }`}
                        >
                          {c.name[lang]}
                        </span>
                        {c.highlight && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-deepblack bg-gold px-2 py-0.5 font-semibold">
                            <Check size={11} /> {p.you}
                          </span>
                        )}
                      </td>
                      <td className="p-5 text-sm text-muted2">{c.price[lang]}</td>
                      <td className="p-5 text-sm text-muted2">{c.language[lang]}</td>
                      <td className="p-5 text-sm">
                        {c.localYes ? (
                          <span className="inline-flex items-center gap-2 text-gold">
                            <Check size={15} /> {c.local[lang]}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-2 text-muted2">
                            <Minus size={15} /> {c.local[lang]}
                          </span>
                        )}
                      </td>
                      <td className="p-5 text-sm text-muted2">{c.model[lang]}</td>
                      <td className="p-5 text-sm text-muted2/80">{c.weakness[lang]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* METRICS */}
      <section className="py-24 bg-deepblack">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="text-center mb-14">
            <Overline>{p.metricsOverline}</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold mt-4">
              {p.metricsTitle}
            </h2>
          </Reveal>
          <div className="space-y-10">
            {METRICS.map((m, i) => (
              <Reveal key={m.label.en} delay={0.08 * i}>
                <MetricBar label={m.label[lang]} value={m.value} delay={i * 150} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
